/**
 * **The single trigger table**, and the matcher generated from it.
 *
 * Split out of `agentQueryGate.ts` only for file size — the table and the
 * matcher stay together on purpose. The design this replaced had *two* regex
 * tables that had to agree, nothing made them agree, and the drift between them
 * was two of its five Criticals. Here the matcher is derived from the array
 * below, so disagreement is impossible by construction rather than by
 * discipline. If you are about to add a second list in either file: don't.
 *
 * The scan that consumes this lives in `agentQueryGate.ts`; read its header for
 * why nothing is normalised before matching.
 */

/**
 * What kind of act the token stands for. Two kinds, because the confirmation
 * dialog says something genuinely different for each — see
 * `DangerousQueryDialog.tsx`, which switches on the reason text.
 */
export type AgentTriggerKind = 'destructive' | 'opaque-execution';

export type AgentTrigger = {
  /** The literal token as it appears in SQL. Matched case-insensitively. */
  readonly token: string;
  readonly kind: AgentTriggerKind;
  /**
   * Match as a bare substring instead of boundary-anchored. Only `EXEC` needs
   * it. It is what reaches every *letter*-adjacent spelling — `EXECUTE dbo.p`,
   * `sp_executesql`, PL/pgSQL `EXECUTE format(…)` — which no boundary rule can,
   * and it subsumes `compound` below, so the two are never set together. The
   * cost is confirming on `sys.dm_exec_query_stats` (47 such names in this
   * SQL Server's catalog) and on `executions`, and that cost was already in the
   * build this replaces. `agentQueryGate.test.ts` pins `sp_executesql`,
   * `EXECUTE dbo.p` and `dblink_exec`, so dropping `bare` reds.
   */
  readonly bare?: true;
  /**
   * Let `_` open and close this token, so it is found inside a compound
   * identifier: `pg_drop_replication_slot`, `lo_truncate`, `load_extension`,
   * `sp_rename`, `REPAIR_ALLOW_DATA_LOSS`. Off by default — see the boundary
   * note below for the rule, the price of setting it, and the residual it
   * leaves.
   */
  readonly compound?: true;
  /** Why this token is here. Kept beside it so nobody prunes it blind. */
  readonly why: string;
};

/**
 * **`_` separates for a `compound` token, and only for a `compound` token.**
 *
 * `\b` — the obvious choice, and what this file shipped first — cannot cross an
 * underscore, so `pg_drop_replication_slot`, `lo_truncate`,
 * `pg_replication_origin_drop` and `DBMS_AQADM.PURGE_QUEUE_TABLE` were silent
 * while `DROP`, `TRUNCATE` and `PURGE` sat in the table. PostgreSQL and Oracle
 * name a large fraction of their destructive routines in snake_case, so that
 * was systematic, not four curiosities. Widening the boundary for *every* token
 * closes all four — and this file shipped that first — but it costs far more
 * than the fixed corpora can see: on 240 catalog-introspection reads extracted
 * from this app's own metadata, security, session, performance and Object
 * Explorer SQL, the global widening takes the gate-level confirm rate from 12
 * (5.00 %) to 23 (9.58 %), and `SELECT name, create_date FROM sys.databases`
 * starts raising a dialog.
 *
 * **The rule for setting `compound`:** set it when the token appears as an
 * underscore-delimited segment of a real identifier that is itself destructive,
 * and only then. Each of the ten below names its witness in `why`; each was
 * priced on its own first, and each is held by a test that clears the flag and
 * requires its witness to go silent. Measured per flag, gate level, marginal
 * against the build without it: **0 of 538** fixed-corpus entries, **0 of
 * 15 726** English-prose queries, **0 of 240** catalog reads, and **0** on 5 727
 * columns of 613 real user-designed tables — 5 666 of those columns read live
 * out of all five engines, the rest from this repo's own seed SQL. At identifier
 * level, against 75 726 distinct names read live from every catalog and routine
 * namespace on the five engines, the ten take the count the agent detector
 * matches from 564 (0.74 %) to 1 255 (1.66 %); the global widening takes it to
 * 3 618 (4.78 %).
 *
 * `WRITETEXT` and `UPDATETEXT` were proposed and are deliberately NOT flagged:
 * neither occurs even as a *substring* in any of those 75 726 names, so the flag
 * would change nothing anywhere and could not be proved load-bearing. Both
 * statements still confirm through their plain `\b` entries.
 *
 * `DELETE` is the one candidate that was priced and **rejected**: it is the only
 * verb of its family with a measured cost, +1 of those 240 catalog reads, and it
 * lands on the commonest catalog read an agent performs — foreign-key
 * introspection selects `rc.DELETE_RULE AS delete_action`. Of the ~296 names it
 * would newly reach, **36** match `sp_delete_*` / `xp_delete_*` literally and 60
 * are `sp_`/`xp_` routines containing `delete` anywhere — the reading matters,
 * so both are given — and those are already caught in the `EXEC sp_delete_job`
 * form an agent writes.
 *
 * **The residual, stated rather than discovered later** (same family as the
 * limit in `agent-gate-coverage/verbs.ts`): a compound whose verb segment is not
 * flagged stays silent. Split by invocation form, because the two are not
 * equally exposed — measured at gate level on this build:
 *
 * | form | example | verdict |
 * |---|---|---|
 * | function / PL-SQL call, no `EXEC` to catch | `BEGIN DBMS_STATS.DELETE_TABLE_STATS(user, T); END;` | **silent** |
 * | T-SQL proc, first statement of a batch | `sp_delete_job @job_name = 'x'` | **silent** |
 * | the same proc as an agent writes it | `EXEC msdb.dbo.sp_delete_job @job_name = 'x'` | confirms via `bare` |
 *
 * The examples above are `DELETE`-family, but the limit is not: every unflagged
 * verb has the same gap, and `SELECT pg_create_physical_replication_slot('s')`,
 * `DBMS_RESOURCE_MANAGER.GRANT_SYSTEM_PRIVILEGE(…)` and
 * `sp_revoke_publication_access 'p','u'` are silent here for `CREATE`, `GRANT`
 * and `REVOKE` exactly as the `DELETE` ones are. All of them are silent on every
 * build before this one too, so this is the design's standing limit, not a new
 * hole.
 *
 * The cost of closing it is **not** on user schemas — of 1 873 distinct real
 * user column and table names, `delete` occurs 4 times and `load` 3, every one
 * of them letter-adjacent (`IsDeleted`, `Payload`) and therefore unmatchable,
 * and zero as a `_`-delimited segment. The cost is on **catalog metadata**:
 * `delete_rule` is a real name in that 75 726 population and is what a `DELETE`
 * flag would flip. Decide it with a measurement, not by widening the default.
 *
 * `is_copy_only` and `load_time` belong on the other side of that ledger: this
 * build **already** confirms on both, because `COPY` and `LOAD` are flagged. They
 * are a price already paid, not a price of closing what remains — and they are
 * the visible edge of a wider class. Of the 216 names the three new flags newly
 * reach, **129 are settings, views, columns and parameters rather than callables**,
 * so read-only engine metadata now confirms: `SELECT … is_copy_only FROM
 * msdb.dbo.backupset`, `pg_stat_progress_copy`, `pg_conf_load_time()`,
 * `@@innodb_buffer_pool_load_at_startup`, `V$DATAFILE_COPY`, `v$sql.first_load_time`,
 * and warehouse columns such as `dw_load_date` / `etl_load_id`. That is accepted:
 * the gate is deliberately asymmetric, and `SELECT load_extension('/tmp/x.so')` is
 * arbitrary code execution. But no corpus here contained a warehouse schema or a
 * monitoring workload, so these flags are proved cheap on what was measured — not
 * cheap everywhere. Real-traffic `{confirmed, reason, engine}` telemetry settles it.
 *
 * Letter adjacency is rejected under both forms, which is what keeps this
 * cheaper than the lexical build on ordinary reads: `DROP` does not match
 * `dropped`, `UPDATE` does not match `updated_at` or `updates`, `COPY` does not
 * match `copyright`.
 *
 * Plain `INSERT` is deliberately **absent**: it is additive, and pulling it in
 * would leave a standing grant covering nothing but `SELECT`. The non-additive
 * upsert forms are still caught — `ON DUPLICATE KEY UPDATE` and `ON CONFLICT …
 * DO UPDATE` by `UPDATE`, `INSERT OR REPLACE` by `REPLACE`.
 *
 * `SET` and `DISCARD` are deliberately absent too: an agent legitimately sets
 * session state constantly, and `SET` must stay non-destructive or the two
 * session-setting probes in the bypass corpus become untestable.
 *
 * Every entry is proved load-bearing by `agentGateCoverage.test.ts`, which
 * rebuilds the matcher without it and requires its probe to go silent, and
 * every `compound` flag the same way, against the witness named in its `why`.
 */
export const AGENT_TRIGGERS: readonly AgentTrigger[] = [
  { token: 'DELETE', kind: 'destructive', why: 'removes rows' },
  { token: 'UPDATE', kind: 'destructive', why: 'overwrites rows; also catches both upsert forms' },
  { token: 'TRUNCATE', kind: 'destructive', compound: true, why: 'empties a table; lo_truncate' },
  { token: 'DROP', kind: 'destructive', compound: true, why: 'removes an object; pg_drop_replication_slot' },
  { token: 'ALTER', kind: 'destructive', why: 'reshapes an object; ALTER ROLE grants capability' },
  { token: 'MERGE', kind: 'destructive', why: 'its WHEN MATCHED branch deletes or overwrites' },
  { token: 'REPLACE', kind: 'destructive', why: 'REPLACE INTO is a delete-then-insert (MySQL/SQLite)' },
  {
    token: 'RENAME',
    kind: 'destructive',
    compound: true,
    why: 'the table is gone under its name (MySQL/Oracle); sp_rename, CTXSYS…RENAME_TABLES',
  },
  { token: 'CREATE', kind: 'destructive', why: 'CREATE ROLE|USER|LOGIN hands out capability' },
  { token: 'BACKUP', kind: 'destructive', why: 'BACKUP … TO DISK exfiltrates a whole database' },
  { token: 'RESTORE', kind: 'destructive', why: 'overwrites an entire database' },
  {
    token: 'ATTACH',
    kind: 'destructive',
    compound: true,
    why: 'cross-database read (verified live on SQLite); sp_attach_db',
  },
  { token: 'DETACH', kind: 'destructive', why: 'the ATTACH counterpart' },
  { token: 'VACUUM', kind: 'destructive', why: 'VACUUM INTO writes a full copy (verified live)' },
  {
    token: 'COPY',
    kind: 'destructive',
    compound: true,
    why: 'COPY … TO/FROM PROGRAM or a file, both directions; xp_copy_file',
  },
  {
    token: 'PURGE',
    kind: 'destructive',
    compound: true,
    why: 'PURGE RECYCLEBIN (Oracle); Kusto `.purge`; DBMS_STATS.PURGE_STATS',
  },
  { token: 'INSTALL', kind: 'destructive', why: 'INSTALL PLUGIN loads server-side code' },
  { token: 'UNINSTALL', kind: 'destructive', why: 'the INSTALL counterpart' },
  {
    token: 'LOAD',
    kind: 'destructive',
    compound: true,
    why: 'LOAD DATA INFILE reads a server file into a table; load_extension',
  },
  { token: 'BULK', kind: 'destructive', why: 'BULK INSERT reads an attacker-chosen UNC path' },
  { token: 'REFRESH', kind: 'destructive', why: 'REFRESH MATERIALIZED VIEW overwrites a matview' },
  {
    token: 'REASSIGN',
    kind: 'destructive',
    compound: true,
    why: 'REASSIGN OWNED moves every object to another role; MDSYS.SDO_CS.REASSIGN_SRID',
  },
  { token: 'LOCK', kind: 'destructive', why: 'LOCK TABLE can stall every other session' },
  { token: 'CLEAR', kind: 'destructive', why: 'Kusto `.clear table … data`' },
  { token: 'GRANT', kind: 'destructive', why: 'widens privilege — worse than any single DELETE' },
  { token: 'REVOKE', kind: 'destructive', why: "removes privilege, including the caller's own" },
  { token: 'DENY', kind: 'destructive', why: 'the third T-SQL privilege verb beside GRANT/REVOKE' },
  { token: 'OUTFILE', kind: 'destructive', why: 'SELECT … INTO OUTFILE, a mid-statement file sink' },
  { token: 'DUMPFILE', kind: 'destructive', why: 'the OUTFILE sibling' },
  { token: 'PROGRAM', kind: 'destructive', why: 'COPY … TO/FROM PROGRAM shells out as the server user' },
  { token: 'WRITABLE_SCHEMA', kind: 'destructive', why: 'PRAGMA writable_schema corrupts the schema' },
  { token: 'LO_EXPORT', kind: 'destructive', why: 'server-side file write (PostgreSQL large objects)' },
  { token: 'LO_IMPORT', kind: 'destructive', why: 'server-side file read' },
  { token: 'LO_UNLINK', kind: 'destructive', why: 'deletes a large object; same family as LO_EXPORT' },
  { token: 'LO_PUT', kind: 'destructive', why: 'overwrites a large object at an offset' },
  { token: 'LOWRITE', kind: 'destructive', why: 'writes into an open large object' },
  // `lo_truncate` is NOT an entry: it is reached by TRUNCATE's `compound` flag,
  // and a redundant entry cannot be proved load-bearing. That omission is safe
  // only while TRUNCATE carries the flag — which is why `lo_truncate` is
  // TRUNCATE's recorded witness, so clearing the flag reds rather than silently
  // re-opening this.
  {
    token: 'PG_TERMINATE_BACKEND',
    kind: 'destructive',
    why: "kills another session — LOCK's own denial-of-service rationale",
  },
  {
    token: 'KILL',
    kind: 'destructive',
    compound: true,
    why: 'the MySQL/T-SQL sibling of pg_terminate_backend; sp_xtp_kill_active_transactions',
  },
  { token: 'DBCC', kind: 'destructive', why: 'DBCC CHECKDB … REPAIR_ALLOW_DATA_LOSS; DBCC WRITEPAGE' },
  {
    token: 'REPAIR',
    kind: 'destructive',
    compound: true,
    why: 'REPAIR TABLE … USE_FRM can lose rows (MySQL); DBCC … REPAIR_ALLOW_DATA_LOSS',
  },
  { token: 'OPTIMIZE', kind: 'destructive', why: 'OPTIMIZE TABLE rebuilds the table under a lock' },
  { token: 'WRITETEXT', kind: 'destructive', why: "overwrites a column's contents (T-SQL)" },
  { token: 'UPDATETEXT', kind: 'destructive', why: 'the WRITETEXT sibling; UPDATE cannot reach it' },
  {
    token: 'SP_CONFIGURE',
    kind: 'destructive',
    why: "sp_configure 'xp_cmdshell', 1 is the standard escalation to OS commands",
  },
  {
    token: 'EXEC',
    kind: 'opaque-execution',
    bare: true,
    why: 'dynamic SQL; bare so it reaches dblink_exec and EXEC_DDL_STATEMENT',
  },
  { token: 'CALL', kind: 'opaque-execution', why: 'a procedure whose body this app cannot read' },
];

const escapeForRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The boundary, spelled out once, in two forms.
 *
 * `\b` is the default. For a `compound` entry the anchors become
 * `[A-Za-z0-9]`-negative lookarounds instead, which differ from `\b` in exactly
 * one way: `_` stops counting as part of the word, so the token is found inside
 * a compound identifier. Digits stay in either form, so `DROP2` and `2DROP`
 * never match, and a letter still blocks a match under both.
 */
const boundedSource = (trigger: AgentTrigger): string => {
  const token = escapeForRegExp(trigger.token);
  return trigger.compound
    ? `(?<![A-Za-z0-9])${token}(?![A-Za-z0-9])`
    : `\\b${token}\\b`;
};

/**
 * Compile a matcher from a table. Production passes `AGENT_TRIGGERS`; the tests
 * pass subsets, which is how every entry above is proved load-bearing — drop
 * one and its probe has to go silent.
 *
 * No `g` flag: a shared global regex carries `lastIndex` between calls, and a
 * gate whose answer depends on the previous call is not a gate.
 */
export const buildAgentTriggerMatcher = (triggers: readonly AgentTrigger[]): RegExp =>
  new RegExp(
    triggers
      .map((trigger) => (trigger.bare ? escapeForRegExp(trigger.token) : boundedSource(trigger)))
      .join('|'),
    'i'
  );
